import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  defaultGetNextPageParam,
  sortObjectKeys,
} from '@vindr/utils/helpers/common.helper';
import {
  createInstitution,
  createSSOInstitution,
  deleteInstitution,
  getDetailInstitution,
  getInstitutions,
  ParamsCreateInstitution,
  ParamsUpdateInstitution,
  updateInstitution,
} from '../apis/institution.api';
import { queryKey } from '../constants/queryClientKey';

export function useGetInstitutions(
  params: Parameters<typeof getInstitutions>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.institution, sortObjectKeys(params)],
    () => getInstitutions(params),
    {
      keepPreviousData: true,
      ...options,
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getInstitutions>>>;
}

export function useGetInstitutionsInfinity(
  params: Parameters<typeof getInstitutions>[0],
  options?: UseInfiniteQueryOptions
) {
  const queryInfo = useInfiniteQuery(
    [queryKey.institution, sortObjectKeys(params)],
    (e) =>
      getInstitutions({
        ...params,
        offset: (e.pageParam || 0) + (params?.offset || 0),
      }),
    {
      getNextPageParam: defaultGetNextPageParam,
      keepPreviousData: true,
      ...options,
    }
  ) as UseInfiniteQueryResult<Awaited<ReturnType<typeof getInstitutions>>>;

  const data = queryInfo.data?.pages.map((page) => page?.data || []).flat();

  return {
    ...queryInfo,
    data,
  };
}

export function useGetDetailInstitution(id, config) {
  return useQuery(
    [queryKey.detail_institution, id],
    () => getDetailInstitution(id),
    {
      keepPreviousData: true,
      ...config,

      onSuccess: (data) => {
        config?.onSuccess && config?.onSuccess(data.data);
      },
    }
  );
}

export function useCreateInstitution() {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: ParamsCreateInstitution) => createInstitution(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.institution]);
      },
      //? Maybe can use onSettled for both onSuccess and onError
      onError: (err) => {
        console.error('useCreateInstitution', err);
      },
    }
  );
}

export function useCreateSSOInstitution() {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: ParamsCreateInstitution) => createSSOInstitution(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.institution]);
      },
      //? Maybe can use onSettled for both onSuccess and onError
      onError: (err) => {
        console.error('useCreateSSOInstitution', err);
      },
    }
  );
}

export function useUpdateInstitution() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, data }: { id: string; data: ParamsUpdateInstitution }) =>
      updateInstitution(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.institution]);
      },

      onError: (err) => {
        console.error('useUpdateInstitution', err);
        queryClient.invalidateQueries([queryKey.institution]);
      },
    }
  );
}

export function useDeleteInstitution() {
  const queryClient = useQueryClient();

  return useMutation(async (id: string) => deleteInstitution(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.institution]);
    },

    onError: (err) => {
      console.error('useDeleteInstitution', err);
      queryClient.invalidateQueries([queryKey.institution]);
    },
  });
}
